import type { SearchSemanticQuery } from "@/lib/blog/search-types";

const QUERY_PHRASE_REWRITES: ReadonlyArray<[RegExp, string]> = [
  [/\bintegrated programme\b/g, "ip"],
  [/\bjunior college\b/g, "jc"],
  [/\ba levels?\b/g, "alevel"],
  [/\bo levels?\b/g, "olevel"],
  [/\bmathematics\b/g, "maths"],
  [/\bmath\b/g, "maths"],
  [/\bpracticals?\b/g, "practical"],
  [/\bexperiments?\b/g, "experiment"],
  [/\bexperimnt\b/g, "experiment"],
  [/\bexpermnt\b/g, "experiment"],
  [/\blaboratories\b/g, "lab"],
  [/\blaboratory\b/g, "lab"],
];

const TOKEN_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  ip: ["integrated", "programme"],
  integrated: ["ip", "programme"],
  programme: ["ip", "integrated"],
  jc: ["junior", "college", "h2", "alevel"],
  junior: ["jc", "college", "h2", "alevel"],
  college: ["jc", "junior", "h2", "alevel"],
  h2: ["alevel", "jc"],
  alevel: ["h2", "h1", "jc"],
  olevel: ["secondary", "sec"],
  practical: ["experiment", "lab"],
  experiment: ["practical", "lab"],
  lab: ["practical", "experiment"],
  maths: ["mathematics", "math"],
  mathematics: ["maths", "math"],
  math: ["maths", "mathematics"],
  physics: ["phys"],
  phys: ["physics"],
  chemistry: ["chem"],
  chem: ["chemistry"],
  biology: ["bio"],
  bio: ["biology"],
  scholarship: ["bursary", "financial", "aid"],
  bursary: ["scholarship", "financial", "aid"],
};

const TERM_REGEX = /[a-z0-9]+/g;

function normalizeBlogSearchBaseText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9\s-]+/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function applyPhraseRewrites(value: string): string {
  let normalized = value;
  for (const [pattern, replacement] of QUERY_PHRASE_REWRITES) {
    normalized = normalized.replace(pattern, replacement);
  }
  return normalized;
}

export function normalizeBlogSearchText(value: string): string {
  let normalized = normalizeBlogSearchBaseText(value);
  normalized = applyPhraseRewrites(normalized);
  return normalized.replace(/\s+/g, " ").trim();
}

export function tokenizeSearchText(value: string): string[] {
  const matches = value.match(TERM_REGEX);
  if (!matches) return [];
  return matches.filter((token) => token.length > 1);
}

function tokenVariants(token: string): string[] {
  const variants = new Set<string>([token]);

  if (token.endsWith("ies") && token.length > 4) {
    variants.add(`${token.slice(0, -3)}y`);
  } else if (token.endsWith("s") && token.length > 4 && !token.endsWith("ss")) {
    variants.add(token.slice(0, -1));
  }

  return [...variants];
}

export function expandTokenGroup(token: string): string[] {
  const expanded = new Set<string>();
  for (const variant of tokenVariants(token)) {
    expanded.add(variant);
    const synonyms = TOKEN_SYNONYMS[variant];
    if (!synonyms) continue;
    for (const synonym of synonyms) {
      for (const synonymVariant of tokenVariants(synonym)) {
        expanded.add(synonymVariant);
      }
    }
  }
  return [...expanded];
}

export function buildSearchExplanationTerms(
  queryTerms: readonly string[]
): string[] {
  const seen = new Set<string>();
  const explanationTerms: string[] = [];

  for (const rawTerm of queryTerms) {
    const normalizedTerm = normalizeBlogSearchText(rawTerm);
    if (!normalizedTerm) continue;

    const expandedTerms = [normalizedTerm, ...expandTokenGroup(normalizedTerm)];
    for (const term of expandedTerms) {
      if (!term || term.length <= 1 || seen.has(term)) continue;
      seen.add(term);
      explanationTerms.push(term);
    }
  }

  return explanationTerms;
}

export function buildSearchSemanticQuery(
  query: string,
  options?: { maxTerms?: number }
): SearchSemanticQuery {
  const normalizedQuery = normalizeBlogSearchText(query);
  const rawQueryTerms = tokenizeSearchText(normalizedQuery);
  const limitedQueryTerms =
    typeof options?.maxTerms === "number"
      ? rawQueryTerms.slice(0, options.maxTerms)
      : rawQueryTerms;
  const queryTerms = [...new Set(limitedQueryTerms)];

  return {
    normalizedQuery,
    queryTerms,
    explanationTerms: buildSearchExplanationTerms(queryTerms),
    tokenGroups: queryTerms.map((term) => expandTokenGroup(term)),
  };
}

function normalizeSearchExplanationTerm(term: string): string | null {
  const normalized = normalizeBlogSearchText(term);
  return normalized.length > 1 ? normalized : null;
}

export function collectSearchExplanationMatches({
  explanationTerms,
  text,
}: {
  explanationTerms: readonly string[];
  text: string;
}): string[] {
  const normalizedText = normalizeBlogSearchText(text);
  if (!normalizedText) return [];

  const seen = new Set<string>();
  const matched: string[] = [];

  for (const rawTerm of explanationTerms) {
    const term = normalizeSearchExplanationTerm(rawTerm);
    if (!term || seen.has(term)) continue;
    if (!hasTokenBoundaryPhraseMatch(normalizedText, term)) continue;
    seen.add(term);
    matched.push(term);
  }

  return matched;
}

function escapeBoundaryRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function hasTokenBoundaryPhraseMatch(
  text: string,
  phrase: string
): boolean {
  if (!text || !phrase) return false;
  return new RegExp(
    `(^|[^a-z0-9])(${escapeBoundaryRegex(phrase)})(?=$|[^a-z0-9])`,
    "i"
  ).test(text);
}
